import { pageTitle } from '@palaccount/config';
import { ArrowLeft, Pause, ScanLine, ShoppingCart, Tag, Trash2, Undo2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { Amount } from '@/components/amount';
import { Pager } from '@/components/pager';
import { Button } from '@/components/ui/button';
import { usePageRange } from '@/lib/paging';
import { localizeServerMessage } from '@/lib/server-messages';
import { useBaseCurrency } from '@/lib/session';
import { cn } from '@/lib/utils';

import {
  POS_PAGE_SIZE,
  useCloseShift,
  useCompleteSale,
  useCreateRegister,
  useHeldSale,
  useOpenShift,
  useParkCart,
  useParkedCarts,
  useProducts,
  useRefund,
  useSession,
  useShiftSales,
  useUnpark,
} from './api';
import { useWarehouses } from '@/features/inventory/stock-api';

import { addToCart, bestMatch, removeLine, setLineDiscount, setQty, type CartLine } from './cart';
import {
  blocksFor,
  fileScan,
  lineTracking,
  nextScan,
  returningTracking,
  scannedFor,
  unfileScan,
} from './tracking';
import { blockKey, blockUnits, textFromStored } from '@/features/sales/tracking-capture';
// Every amount this screen shows or sends is computed in till-math, which is
// inside the money/no-raw-money-arithmetic fence. Nothing on this page may
// add, subtract, multiply or divide an amount itself.
import {
  cardPart,
  cartSummary,
  lineNetOf,
  changeDue,
  drawerDifference,
  isDrawerOut,
  isPayable,
  isShortOnCash,
  refundUnitPrice,
} from './till-math';

/**
 * SAL-06 — the point of sale.
 *
 * "A standalone full-screen application with its own route and no dashboard
 * shell — no sidebar, no top bar, no breadcrumb." That is why this page is
 * mounted OUTSIDE AppShell in the router rather than styled to look empty: a
 * counter screen with a menu down the side is a counter screen a cashier can
 * get lost in.
 *
 * Three screens live behind the one route — the gate, the sale, and returns —
 * and the returns screen is a MODE, not a tab, because the spec insists on the
 * difference in its own words: "Refunds are handled separately from sales — no
 * sale line items here."
 */
export function PosPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [registerId, setRegisterId] = useState<string | undefined>(undefined);
  const session = useSession(registerId);
  const [mode, setMode] = useState<'sale' | 'returns'>('sale');

  useEffect(() => {
    document.title = pageTitle(t('pos.title'));
  }, [t]);

  const shift = session.data?.shift ?? null;

  return (
    <div className="flex h-dvh flex-col bg-background">
      <header className="flex items-center gap-3 border-b px-4 py-2">
        <Button variant="ghost" size="icon" aria-label={t('pos.exit')} onClick={() => void navigate('/')}>
          {/* The arrow mirrors in Arabic — logical properties do it for free. */}
          <ArrowLeft className="h-5 w-5 rtl:rotate-180" />
        </Button>
        <div className="min-w-0">
          <p className="text-sm font-semibold">{t('pos.title')}</p>
          <p className="text-xs text-muted-foreground">
            {shift ? `${shift.registerCode} · ${shift.registerName}` : t('pos.noRegister')}
          </p>
        </div>
        {shift ? (
          <div className="ms-auto flex items-center gap-2">
            <CloseShiftButton shift={shift} />
          </div>
        ) : null}
      </header>

      {session.isPending ? (
        <p className="p-8 text-center text-sm text-muted-foreground">{t('common.loading')}</p>
      ) : !shift ? (
        <ShiftGate
          registers={session.data?.registers ?? []}
          registerId={registerId}
          onRegister={setRegisterId}
        />
      ) : mode === 'returns' ? (
        <ReturnsScreen shift={shift} taxRate={session.data!.taxRate} onBack={() => setMode('sale')} />
      ) : (
        <SaleScreen
          shift={shift}
          taxRate={session.data!.taxRate}
          onReturns={() => setMode('returns')}
        />
      )}
    </div>
  );
}

/**
 * §5b — the gate. "Until this button is pressed there is no sale screen at
 * all. Nothing can be sold without an open shift — the gate is STRUCTURAL, not
 * a warning." So this component renders instead of the till, never beside it.
 */
function ShiftGate({
  registers,
  registerId,
  onRegister,
}: {
  registers: { id: string; code: string; name: string }[];
  registerId: string | undefined;
  onRegister: (id: string) => void;
}) {
  const { t } = useTranslation();
  const open = useOpenShift();
  const [cash, setCash] = useState('0.00');
  const chosen = registerId ?? registers[0]?.id;

  return (
    <main className="flex flex-1 flex-col items-center justify-center gap-4 p-8">
      <h1 className="text-2xl font-semibold">{t('pos.openShift')}</h1>
      <p className="max-w-[42ch] text-center text-sm text-muted-foreground">{t('pos.openShiftHint')}</p>

      {registers.length === 0 ? (
        <RegisterSetup />
      ) : null}

      {registers.length > 1 ? (
        <select
          aria-label={t('pos.register')}
          className="rounded-md border bg-background px-3 py-2"
          value={chosen}
          onChange={(e) => onRegister(e.target.value)}
        >
          {registers.map((r) => (
            <option key={r.id} value={r.id}>
              {r.code} · {r.name}
            </option>
          ))}
        </select>
      ) : null}

      <label className="flex flex-col gap-1 text-sm" htmlFor="opening-cash">
        {t('pos.startingCash')}
        <input
          id="opening-cash"
          dir="ltr"
          inputMode="decimal"
          className="w-56 rounded-md border bg-background px-3 py-2 text-end font-mono"
          placeholder="0.00"
          value={cash}
          onChange={(e) => setCash(e.target.value)}
        />
      </label>

      {/*
        * THROUGH THE MAP — the open-shift door's refusals are
        * `POS_MESSAGES.noOpenShift` and `.shiftAlreadyOpen`, both mapped.
        * See the note on the refund strip below for why the wrap and the map
        * entry are two halves and neither localizes anything alone.
        */}
      {open.isError ? (
        <p role="alert" className="text-sm text-destructive">
          {localizeServerMessage(t, (open.error as Error).message, open.error)}
        </p>
      ) : null}

      <Button
        disabled={!chosen || open.isPending}
        onClick={() => chosen && open.mutate({ registerId: chosen, openingCash: cash || '0.00' })}
      >
        {t('pos.openAndSell')}
      </Button>
    </main>
  );
}

/**
 * A fresh company has no register, and the till is unusable until one exists.
 * Rather than a blank screen saying "No register configured", the gate offers
 * to make the first one against a warehouse the company already has — the
 * smallest thing that turns a dead end into a next step.
 */
function RegisterSetup() {
  const { t } = useTranslation();
  const warehouses = useWarehouses();
  const create = useCreateRegister();
  const [code, setCode] = useState('REG-01');
  const [name, setName] = useState('');
  const [warehouseId, setWarehouseId] = useState('');
  const chosen = warehouseId || warehouses.data?.[0]?.id || '';

  return (
    <div className="w-full max-w-sm space-y-3 rounded-lg border p-4">
      <p className="text-sm font-medium">{t('pos.setupRegister')}</p>
      <p className="text-xs text-muted-foreground">{t('pos.setupHint')}</p>
      <label className="flex flex-col gap-1 text-sm" htmlFor="register-code">
        {t('pos.registerCode')}
        <input
          id="register-code"
          dir="ltr"
          className="rounded-md border bg-background px-3 py-2 font-mono"
          value={code}
          onChange={(e) => setCode(e.target.value)}
        />
      </label>
      <label className="flex flex-col gap-1 text-sm" htmlFor="register-name">
        {t('pos.registerName')}
        <input
          id="register-name"
          className="rounded-md border bg-background px-3 py-2"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
      </label>
      <label className="flex flex-col gap-1 text-sm" htmlFor="register-warehouse">
        {t('pos.registerWarehouse')}
        <select
          id="register-warehouse"
          className="rounded-md border bg-background px-3 py-2"
          value={chosen}
          onChange={(e) => setWarehouseId(e.target.value)}
        >
          {warehouses.data?.map((w) => (
            <option key={w.id} value={w.id}>
              {w.code} · {w.name}
            </option>
          ))}
        </select>
      </label>
      {warehouses.data?.length === 0 ? (
        <p className="text-sm text-destructive">{t('pos.needWarehouse')}</p>
      ) : null}
      {/*
        * THROUGH THE MAP — this door raises `POS_MESSAGES.registerCodeTaken`
        * and `.noWarehouse`, both mapped. `noWarehouse` answers with
        * `pos.needWarehouse`, the SAME key the client-side mirror three lines
        * above already uses: one contract sentence, one key, one Arabic.
        */}
      {create.isError ? (
        <p role="alert" className="text-sm text-destructive">
          {localizeServerMessage(t, (create.error as Error).message, create.error)}
        </p>
      ) : null}
      <Button
        className="w-full"
        disabled={!chosen || name.trim().length < 2 || create.isPending}
        onClick={() => create.mutate({ code: code.trim(), name: name.trim(), warehouseId: chosen })}
      >
        {t('pos.createRegister')}
      </Button>
    </div>
  );
}

/**
 * §4's sale screen. The cart's totals block and its buttons are PINNED so they
 * never scroll out of view "however long the cart gets. That is a deliberate
 * counter-screen decision and must be kept."
 */
function SaleScreen({
  shift,
  taxRate,
  onReturns,
}: {
  shift: { id: string; registerId: string };
  taxRate: string;
  onReturns: () => void;
}) {
  const { t } = useTranslation();
  // GEN-107: the till prints in the company's own money, read from the
  // session. A shop has no document currency of its own — SAL-06's contract
  // carries none — so what is stated here is exactly what the sale POSTS in
  // (`pos.service.ts` reads the same fact). Nothing converts (GEN-116).
  const baseCurrency = useBaseCurrency();
  const [search, setSearch] = useState('');
  const [cart, setCart] = useState<CartLine[]>([]);
  const [paying, setPaying] = useState(false);

  /*
   * The till is not a linkable location — it is a `useState` behind an open
   * shift (§9's own note about this screen) — so its page lives in component
   * state rather than the URL, exactly as INV-05 §5.9's modal does.
   *
   * Resetting to page 1 on every new search is not cosmetic here, it is what
   * keeps SCANNING correct: `bestMatch` picks from the rows on screen, and the
   * server orders a barcode-exact hit FIRST, so the match is on page 1 or it
   * is nowhere. Left on page 3, a scan would search the third page of the new
   * term and find nothing. BR-DSH-09-19's reason, at a counter.
   */
  const [productPage, setProductPage] = useState(1);
  const searchProducts = (term: string) => {
    setSearch(term);
    setProductPage(1);
  };
  const products = useProducts(shift.registerId, search, productPage);
  const productRows = products.data?.products ?? [];
  const productRange = usePageRange(products.data, POS_PAGE_SIZE);
  const parked = useParkedCarts(shift.id);
  const park = useParkCart(shift.id);
  const unpark = useUnpark();

  const totals = useMemo(() => cartSummary(cart, taxRate), [cart, taxRate]);

  /**
   * BR-MD-03-17 at a counter. While anything in the cart is unidentified the
   * ONE text box asks for the next identifier instead of searching products:
   * scan the item, scan its serial, scan the next item. Naming one block at a
   * time is deliberate — "which of these four things is this serial for?" is
   * a question a queue cannot afford.
   */
  const pending = useMemo(() => nextScan(cart), [cart]);

  return (
    <main className="grid flex-1 grid-cols-1 overflow-hidden lg:grid-cols-[1.4fr_1fr]">
      <section className="flex flex-col gap-3 overflow-y-auto p-4">
        {pending ? (
          <p
            role="status"
            className="rounded-md border border-amber-400 bg-amber-50 px-3 py-2 text-sm font-medium text-amber-900 dark:border-amber-600 dark:bg-amber-950 dark:text-amber-200"
          >
            {pending.block.kind === 'serial'
              ? t('pos.scanSerialFor', {
                  name: pending.block.name,
                  n: pending.position,
                  of: pending.block.needed ?? 1,
                })
              : t('pos.scanBatchFor', {
                  name: pending.block.name,
                  qty: blockUnits(pending.block),
                })}
          </p>
        ) : null}
        <label className="flex items-center gap-2 rounded-md border px-3 py-2" htmlFor="pos-scan">
          <ScanLine className="h-4 w-4 text-muted-foreground" />
          <span className="sr-only">{pending ? t('pos.scanIdentifier') : t('pos.scan')}</span>
          <input
            id="pos-scan"
            className="flex-1 bg-transparent outline-none"
            placeholder={pending ? t('pos.identifierPlaceholder') : t('pos.scanPlaceholder')}
            aria-label={pending ? t('pos.scanIdentifier') : t('pos.scan')}
            value={search}
            onChange={(e) => searchProducts(e.target.value)}
            onKeyDown={(e) => {
              // §5c: "a barcode scanner that ends with Enter adds the item
              // straight to the cart" — no tap, no list, no reading. The same
              // press files an identifier when one is outstanding, so a
              // serial never needs a different gesture from a barcode.
              if (e.key !== 'Enter') return;
              e.preventDefault();
              if (pending) {
                setCart((c) => fileScan(c, pending, search));
                searchProducts('');
                return;
              }
              // The server orders a barcode-exact hit FIRST and the page was
              // reset when this term was typed, so the true best match is on
              // the rows in hand or it does not exist.
              const hit = bestMatch(productRows, search);
              if (!hit) return;
              setCart((c) => addToCart(c, hit));
              searchProducts('');
            }}
          />
        </label>

        {/*
          A failed fetch and a genuine no-match must not read the same. They
          did once, and "No products match" is what a till showed when the
          catalogue query was broken — a cashier would have retyped the name
          rather than reported an outage.
        */}
        {products.isError ? (
          <p role="alert" className="py-8 text-center text-sm text-destructive">
            {t('common.loadFailed')}
          </p>
        ) : productRange.total === 0 ? (
          // The SERVER's count of the match, not the size of the page: under
          // the old `LIMIT 200` these were the same number until they weren't.
          <p className="py-8 text-center text-sm text-muted-foreground">{t('pos.noProducts')}</p>
        ) : (
          <div
            role="group"
            aria-label={t('pos.products')}
            className="grid gap-2"
            style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))' }}
          >
            {productRows.map((p) => (
              <button
                key={p.itemId}
                type="button"
                className="rounded-lg border p-3 text-start hover:border-primary"
                onClick={() => setCart((c) => addToCart(c, p))}
              >
                <p className="truncate text-sm font-medium">{p.name}</p>
                <Amount value={p.price} currency={baseCurrency} className="text-sm text-muted-foreground" />
                {/* A cashier should learn the tile will ask for a serial by
                    looking at it, not by being stopped at the payment
                    dialog. */}
                {p.tracking !== 'none' || p.autoDeduct ? (
                  <p className="mt-1 truncate text-[11px] text-muted-foreground">
                    {p.tracking === 'serial'
                      ? t('pos.tileSerial')
                      : p.tracking === 'batch'
                        ? t('pos.tileBatch')
                        : t('pos.tileKit', { count: p.components.length })}
                  </p>
                ) : null}
              </button>
            ))}
          </div>
        )}

        {/* The rest of the match, reachable — and countable. */}
        {productRange.pages > 1 ? (
          <Pager
            page={productPage}
            pages={productRange.pages}
            first={productRange.first}
            last={productRange.last}
            total={productRange.total}
            onPage={setProductPage}
            label={t('pos.products')}
          />
        ) : null}

        {(parked.data ?? []).length > 0 ? (
          <div>
            <p className="flex items-center gap-1 text-sm font-medium">
              <Pause className="h-4 w-4" />
              {t('pos.parked', { count: parked.data!.length })}
            </p>
            <div
              role="group"
              aria-label={t('pos.parked', { count: parked.data!.length })}
              className="mt-2 flex flex-wrap gap-2"
            >
              {parked.data!.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  className="rounded-lg border px-3 py-2 text-start text-sm"
                  onClick={() => {
                    // Resuming brings the cart back and clears the row, so a
                    // parked twin can never be left behind to block the close.
                    setCart(
                      c.lines.map((l) => ({
                        itemId: l.itemId,
                        name: l.name,
                        unitPrice: l.unitPrice,
                        qty: Number(l.qty),
                        lineDiscount: l.lineDiscount ?? '0',
                        tracking: l.tracking,
                        autoDeduct: l.autoDeduct,
                        components: l.kitComponents,
                        // What was scanned before the sale was parked comes
                        // back with it, in the shape the cart holds.
                        identifiers: textFromStored({
                          serialNos: l.serialNos ?? null,
                          batchNo: l.batchNo ?? null,
                          components: (l.components ?? []).map((block) => ({
                            componentItemId: block.componentItemId,
                            serialNos: block.serialNos ?? null,
                            batchNo: block.batchNo ?? null,
                          })),
                        }),
                      })),
                    );
                    unpark.mutate(c.id);
                  }}
                >
                  <p className="font-medium">{c.label}</p>
                  <span className="text-xs text-muted-foreground">
                    <Amount value={c.total} currency={baseCurrency} className="text-xs" /> · {t('pos.itemCount', { count: c.lineCount })}
                  </span>
                </button>
              ))}
            </div>
          </div>
        ) : null}
      </section>

      {/* The cart. `overflow-hidden` + the pinned footer is §4's rule. */}
      <section className="flex flex-col overflow-hidden border-s">
        <div className="flex-1 overflow-y-auto p-4">
          {cart.length === 0 ? (
            <p className="flex flex-col items-center gap-2 py-10 text-center text-sm text-muted-foreground">
              <ShoppingCart className="h-6 w-6" />
              {t('pos.emptyCart')}
            </p>
          ) : (
            <ul className="flex flex-col divide-y">
              {cart.map((line) => (
                <li key={line.itemId} className="flex flex-col gap-1 py-2">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium">{line.name}</p>
                      <p className="text-xs text-muted-foreground">
                        <Amount value={line.unitPrice} currency={baseCurrency} className="text-xs" /> {t('pos.each')}
                      </p>
                    </div>
                    <button
                      type="button"
                      aria-label={t('pos.removeLine', { name: line.name })}
                      className="text-muted-foreground hover:text-destructive"
                      onClick={() => setCart((c) => removeLine(c, line.itemId))}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="icon" aria-label={t('pos.less', { name: line.name })}
                      onClick={() => setCart((c) => setQty(c, line.itemId, line.qty - 1))}>−</Button>
                    <input
                      aria-label={t('pos.qtyFor', { name: line.name })}
                      dir="ltr"
                      className="w-16 rounded-md border bg-background px-2 py-1 text-center font-mono"
                      value={line.qty}
                      onChange={(e) => setCart((c) => setQty(c, line.itemId, Number(e.target.value)))}
                    />
                    <Button variant="outline" size="icon" aria-label={t('pos.more', { name: line.name })}
                      onClick={() => setCart((c) => setQty(c, line.itemId, line.qty + 1))}>+</Button>
                    <button
                      type="button"
                      aria-label={t('pos.lineDiscount', { name: line.name })}
                      className="text-muted-foreground hover:text-foreground"
                      onClick={() => {
                        // §5h: an AMOUNT, never a percentage — "a cashier
                        // works in money".
                        const typed = window.prompt(t('pos.discountPrompt', { name: line.name }));
                        if (typed !== null) setCart((c) => setLineDiscount(c, line.itemId, typed || '0'));
                      }}
                    >
                      <Tag className="h-4 w-4" />
                    </button>
                    <span className="ms-auto font-medium">
                      <Amount value={lineNetOf(line)} currency={baseCurrency} />
                    </span>
                  </div>
                  {/* R7 made visible: an auto-deducting kit is not a thing on
                      the shelf, it is these parts. The cashier is about to be
                      asked for one part's serial, so the cart says why. */}
                  {line.autoDeduct && line.components.length > 0 ? (
                    <p className="text-xs text-muted-foreground">
                      {t('pos.kitParts', {
                        parts: line.components.map((c) => c.componentName).join(' · '),
                      })}
                    </p>
                  ) : null}
                  <CartLineIdentifiers
                    line={line}
                    onRemove={(key, value) =>
                      setCart((c) => unfileScan(c, line.itemId, key, value))
                    }
                  />
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Pinned: never scrolls away, however long the cart gets. */}
        <div className="border-t p-4">
          <dl className="mb-2 flex flex-col gap-1 text-sm">
            <div className="flex justify-between"><dt>{t('pos.subtotal')}</dt><dd><Amount value={totals.subtotal} currency={baseCurrency} /></dd></div>
            <div className="flex justify-between text-muted-foreground">
              <dt>{t('pos.tax', { pct: (Number(taxRate) * 100).toFixed(0) })}</dt>
              <dd><Amount value={totals.tax} currency={baseCurrency} /></dd>
            </div>
            <div className="flex justify-between text-base font-semibold"><dt>{t('pos.total')}</dt><dd><Amount value={totals.total} currency={baseCurrency} /></dd></div>
          </dl>
          <div className="mb-2 flex gap-2">
            {/*
              ERR-SAL-06-06 — Hold on an empty cart "did nothing" in the
              prototype, which reads as a broken button rather than a refusal.
              It is disabled instead, so the reason is visible before the tap.
            */}
            <Button
              variant="outline"
              className="flex-1"
              disabled={cart.length === 0}
              onClick={() => {
                park.mutate({
                  label: t('pos.saleLabel', { n: (parked.data?.length ?? 0) + 1 }),
                  lines: cart.map((l) => ({
                    itemId: l.itemId,
                    qty: String(l.qty),
                    unitPrice: l.unitPrice,
                    lineDiscount: l.lineDiscount,
                    // Held with what was already scanned: a cashier who
                    // parked a sale to serve the next customer must not come
                    // back to three empty boxes.
                    ...lineTracking(l),
                  })),
                });
                setCart([]);
              }}
            >
              <Pause className="me-1 h-4 w-4" />
              {t('pos.hold')}
            </Button>
            <Button variant="outline" className="flex-1" onClick={onReturns}>
              <Undo2 className="me-1 h-4 w-4" />
              {t('pos.returns')}
            </Button>
          </div>
          {/* The server refuses an unidentified unit whatever this button
              does (BR-MD-03-17); greying it is so the cashier learns it
              BEFORE the customer's card is in the terminal. */}
          <Button
            className="w-full"
            disabled={cart.length === 0 || pending !== null}
            onClick={() => setPaying(true)}
          >
            {t('pos.pay', { total: totals.total })}
          </Button>
        </div>
      </section>

      {paying ? (
        <PaymentDialog
          shift={shift}
          cart={cart}
          total={totals.total}
          onClose={() => setPaying(false)}
          onDone={() => {
            setPaying(false);
            setCart([]);
          }}
        />
      ) : null}
    </main>
  );
}

/**
 * What has already been scanned against a cart line, as chips.
 *
 * A counter needs this for one reason: a mis-scan. Without a visible list and
 * an × on each, the only way out of "that was the wrong serial" is deleting
 * the line and starting the item again with a customer watching.
 */
function CartLineIdentifiers({
  line,
  onRemove,
}: {
  line: CartLine;
  onRemove: (key: string, value: string) => void;
}) {
  const { t } = useTranslation();
  const blocks = blocksFor(line);
  if (blocks.length === 0) return null;

  return (
    <div className="flex flex-col gap-1">
      {blocks.map((block) => {
        const key = blockKey(block);
        const scanned = scannedFor(line, key);
        if (scanned.length === 0) return null;
        return (
          <div key={key} className="flex flex-wrap items-center gap-1">
            <span className="text-xs text-muted-foreground">
              {block.componentItemId === null ? t('pos.identified') : block.name}
            </span>
            {scanned.map((value) => (
              <button
                key={value}
                type="button"
                aria-label={t('pos.removeIdentifier', { value })}
                title={t('pos.removeIdentifier', { value })}
                className="rounded border bg-muted px-1.5 py-0.5 font-mono text-[11px] hover:border-destructive hover:text-destructive"
                onClick={() => onRemove(key, value)}
              >
                <bdi dir="ltr">{value}</bdi> ×
              </button>
            ))}
          </div>
        );
      })}
    </div>
  );
}

/** §5f — the payment dialog, and the receipt that follows it (§5g). */
function PaymentDialog({
  shift,
  cart,
  total,
  onClose,
  onDone,
}: {
  shift: { id: string };
  cart: CartLine[];
  total: string;
  onClose: () => void;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  // GEN-107: the till prints in the company's own money, read from the
  // session. A shop has no document currency of its own — SAL-06's contract
  // carries none — so what is stated here is exactly what the sale POSTS in
  // (`pos.service.ts` reads the same fact). Nothing converts (GEN-116).
  const baseCurrency = useBaseCurrency();
  const complete = useCompleteSale(shift.id);
  const [method, setMethod] = useState<'cash' | 'card' | 'split'>('cash');
  const [tendered, setTendered] = useState('');
  const [cashPart, setCashPart] = useState('');
  const [receipt, setReceipt] = useState<{ saleNo: string; total: string; change: string } | null>(null);

  // BR-SAL-06-07/-08: change is shown BEFORE the sale completes, and the sale
  // cannot complete while the cash is short.
  const change = changeDue(tendered, total);
  const shortOnCash = method === 'cash' && isShortOnCash(tendered, total);

  if (receipt) {
    return (
      <Dialog title={t('pos.saleComplete')} onClose={onDone}>
        <p className="font-mono text-lg">{receipt.saleNo}</p>
        <dl className="my-3 flex justify-between gap-6 text-sm">
          <div><dt className="text-muted-foreground">{t('pos.total')}</dt><dd className="text-lg font-semibold"><Amount value={receipt.total} currency={baseCurrency} /></dd></div>
          <div><dt className="text-muted-foreground">{t('pos.change')}</dt><dd className="text-lg font-semibold"><Amount value={receipt.change} currency={baseCurrency} /></dd></div>
        </dl>
        <p className="text-sm">{t('pos.sendReceipt')}</p>
        <div className="mt-2 flex flex-wrap gap-2">
          {/*
           * SAL-06 §5 row 46 draws FOUR receipt buttons. Two of them ship —
           * Print and Skip. Email and SMS were RETIRED by delegated ruling D34
           * (27 Aug 2026), and this note is their reversal path.
           *
           * ── WHY THEY WENT ───────────────────────────────────────────────
           *
           * EC-19-59 gives a control two states and no third: "Every one must
           * be wired or removed. A button that does nothing teaches a user
           * that the product is broken." Wiring was closed on both, by
           * standing rules rather than by effort:
           *
           *   EMAIL — delegated ruling D15 closes email to three cases
           *   (invitations, password resets, and statements a user explicitly
           *   sends), which the product serves with six templates. A till
           *   receipt is none of them, and `apps/api/test/email-census.spec.ts`
           *   goes red on a seventh. Wiring it would have broken D15.
           *
           *   SMS — no SMS provider exists anywhere in the stack and
           *   `Tech Stack/DECISION.md` names none. Signing one is an owner
           *   action, not a build one.
           *
           * Removal is the only arm that breaks no standing rule. The flow
           * stays complete either way: Print and Skip mean the cashier is
           * never stuck on this dialog.
           *
           * ── HOW THEY COME BACK ──────────────────────────────────────────
           *
           * This is a retirement, not a deletion. The spec row's Email and SMS
           * cells carry a "Retired by delegated ruling" notice rather than
           * being struck, so the requirement is still on the page.
           *
           * Either trigger restores its own button TOGETHER WITH THE WORKER
           * BEHIND IT — never the button alone:
           *
           *   • a receipts-email head added to the email census DELIBERATELY,
           *     as an extension of D15 → Email returns;
           *   • an SMS provider signed and named in DECISION.md → SMS returns.
           *
           * A button restored ahead of its worker is inert again, which is the
           * exact state EC-19-59 forbids and the one D34 has just cleared.
           * `ec1959-control-census.test.ts` asserts ZERO inert controls
           * product-wide, so re-adding either button ahead of its worker turns
           * that file red and names the control.
           *
           * The keys `pos.email`, `pos.sms` and `pos.soon` were deliberately
           * left in both dictionaries, Arabic included, so a reversal costs a
           * call site and not a translation.
           *
           * TODO(receipt-delivery): Print still wants a print stylesheet.
           */}
          <Button variant="outline" onClick={() => window.print()}>{t('pos.print')}</Button>
          <Button onClick={onDone}>{t('pos.skip')}</Button>
        </div>
      </Dialog>
    );
  }

  return (
    <Dialog title={t('pos.payment')} onClose={onClose}>
      <p className="text-sm text-muted-foreground">{t('pos.amountDue')}</p>
      <p className="text-2xl font-semibold"><Amount value={total} currency={baseCurrency} /></p>

      <div className="my-3 flex gap-2">
        {(['cash', 'card', 'split'] as const).map((m) => (
          <Button key={m} variant={method === m ? 'default' : 'outline'} onClick={() => setMethod(m)}>
            {t(`pos.method_${m}`)}
          </Button>
        ))}
      </div>

      {method === 'cash' ? (
        <label className="flex flex-col gap-1 text-sm" htmlFor="pos-tendered">
          {t('pos.tendered')}
          <input
            id="pos-tendered"
            dir="ltr"
            inputMode="decimal"
            className="rounded-md border bg-background px-3 py-2 text-end font-mono"
            value={tendered}
            onChange={(e) => setTendered(e.target.value)}
          />
          <Button variant="outline" size="sm" onClick={() => setTendered(total)}>{t('pos.exact')}</Button>
          <span className="text-sm">
            {t('pos.change')}: <Amount value={change} currency={baseCurrency} />
          </span>
        </label>
      ) : method === 'card' ? (
        <p className="text-sm text-muted-foreground">{t('pos.waitingCard')}</p>
      ) : (
        <label className="flex flex-col gap-1 text-sm" htmlFor="pos-cash-part">
          {t('pos.cashAmount')}
          <input
            id="pos-cash-part"
            dir="ltr"
            inputMode="decimal"
            className="rounded-md border bg-background px-3 py-2 text-end font-mono"
            value={cashPart}
            onChange={(e) => setCashPart(e.target.value)}
          />
          <span className="text-sm">
            {t('pos.onCard')}: <Amount value={cardPart(total, cashPart)} currency={baseCurrency} />
          </span>
        </label>
      )}

      {shortOnCash && tendered !== '' ? (
        <p role="alert" className="mt-2 text-sm text-destructive">{t('pos.tenderedShort')}</p>
      ) : null}
      {complete.isError ? (
        // THROUGH THE MAP — the checkout door raises `emptyCart`,
        // `tenderedShort`, `saleNotEditable` and `outOfStock`. The first three
        // are mapped; `outOfStock` names the item and is parameterised, so it
        // passes through in English by the documented policy — wrapping does
        // not harm it, because `localizeServerMessage` returns anything it does
        // not know unchanged.
        <p role="alert" className="mt-2 text-sm text-destructive">
          {localizeServerMessage(t, (complete.error as Error).message, complete.error)}
        </p>
      ) : null}

      <Button
        className="mt-4 w-full"
        disabled={complete.isPending || (method === 'cash' && shortOnCash)}
        onClick={() => {
          const payments =
            method === 'cash'
              ? [{ method: 'cash' as const, amount: total, tendered: tendered || total }]
              : method === 'card'
                ? [{ method: 'card' as const, amount: total }]
                : [
                    { method: 'cash' as const, amount: cashPart || '0.00', tendered: cashPart || '0.00' },
                    { method: 'card' as const, amount: cardPart(total, cashPart) },
                  ];
          complete.mutate(
            {
              lines: cart.map((l) => ({
                itemId: l.itemId,
                qty: String(l.qty),
                unitPrice: l.unitPrice,
                lineDiscount: l.lineDiscount,
                // BR-MD-03-17: what left the shelf, by name. A kit sends
                // `components` and no identifiers of its own (BR-DATA-19).
                ...lineTracking(l),
              })),
              payments: payments.filter((p) => isPayable(p.amount)),
            },
            {
              onSuccess: (sale) =>
                setReceipt({ saleNo: sale.saleNo, total: sale.total, change: sale.changeGiven }),
            },
          );
        }}
      >
        {t('pos.completeSale')}
      </Button>
    </Dialog>
  );
}

/**
 * A till list is a narrow column of tap targets, not a table: 20 fills it and
 * keeps a page under a screenful of thumb travel.
 */
const SALES_PAGE_SIZE = 20;

/**
 * §5i — returns, a MODE and not a tab. The banner states its own rule, and
 * there is no cart on this screen at all.
 */
function ReturnsScreen({
  shift,
  taxRate,
  onBack,
}: {
  shift: { id: string };
  taxRate: string;
  onBack: () => void;
}) {
  const { t } = useTranslation();
  // GEN-107: the till prints in the company's own money, read from the
  // session. A shop has no document currency of its own — SAL-06's contract
  // carries none — so what is stated here is exactly what the sale POSTS in
  // (`pos.service.ts` reads the same fact). Nothing converts (GEN-116).
  const baseCurrency = useBaseCurrency();
  /**
   * The page lives in COMPONENT STATE, not in the URL, and INV-05 §5.9's item
   * history is the precedent: the till is a full-screen kiosk with no shell and
   * no shareable location, so "page 3 of the returns list" is not a place
   * anybody links to. The control is the same `<Pager>` every other list draws.
   */
  const [page, setPage] = useState(1);
  const sales = useShiftSales(shift.id, page, SALES_PAGE_SIZE);
  const range = usePageRange(sales.data, SALES_PAGE_SIZE);
  const refund = useRefund(shift.id);
  const [saleId, setSaleId] = useState<string | null>(null);
  const held = useHeldSale(shift.id, saleId);
  const [qtys, setQtys] = useState<Record<string, number>>({});
  const [restock, setRestock] = useState(true);
  const [method, setMethod] = useState<'cash' | 'card'>('cash');
  /**
   * WHICH unit is coming back, per `${saleLineId}|${componentItemId ?? ''}`.
   *
   * BR-MD-03-17 does not point one way. A serial put back on the shelf
   * unnamed can never be sold again — the ledger would rightly answer "not in
   * this warehouse" for ever — so a restocking refund of a tracked item has
   * to say which one. The chips are the serials THIS sale took off the shelf,
   * so the cashier taps the one in their hand rather than typing it.
   */
  const [comingBack, setComingBack] = useState<Record<string, string[]>>({});

  /**
   * The selected sale comes from the `ids` read, never from the page. Reading
   * it out of the current page is the defect that appears the moment the list
   * is paged: the pane blanks when the sale scrolls off.
   */
  const sale = held.data ?? undefined;
  /**
   * The figure the customer is handed, so it must be the figure the server
   * will post — TAX INCLUDED. It was the pre-tax net once, and a screen that
   * promises $22.10 while the books record $25.42 leaving the drawer sends
   * the cashier home with an unexplainable count. Same arithmetic as the sale
   * screen, through the same function, in the same order: net first, tax on
   * the rounded net, total from the rounded tax.
   */
  const refundNet = (sale?.lines ?? []).map((l) => ({
    itemId: l.itemId,
    name: l.itemName,
    unitPrice: refundUnitPrice(l.lineNet, l.qty),
    qty: qtys[l.id] ?? 0,
    lineDiscount: '0',
  }));
  const refundTotal = cartSummary(refundNet, taxRate).total;

  return (
    <main className="flex flex-1 flex-col overflow-hidden">
      <div className="flex items-center gap-3 border-b bg-muted/40 px-4 py-2">
        <div>
          <p className="text-sm font-semibold">{t('pos.returnsMode')}</p>
          <p className="text-xs text-muted-foreground">{t('pos.returnsRule')}</p>
        </div>
        <Button variant="outline" className="ms-auto" onClick={onBack}>{t('pos.backToSale')}</Button>
      </div>

      <div className="grid flex-1 grid-cols-1 overflow-hidden lg:grid-cols-[1fr_1.4fr]">
        <section className="overflow-y-auto border-e p-4">
          <h2 className="mb-2 text-sm font-medium">{t('pos.pickSale')}</h2>
          <ul className="flex flex-col gap-2">
            {/*
              No `.filter()` here any more: `refundable` is a server predicate,
              because a LIMIT in front of a JavaScript filter returns a page
              that is short by however many refunds it happened to contain.
            */}
            {(sales.data?.sales ?? []).map((s) => (
              <li key={s.id}>
                <button
                  type="button"
                  className={cn('w-full rounded-lg border px-3 py-2 text-start', saleId === s.id && 'border-primary bg-primary/5')}
                  onClick={() => {
                    setSaleId(s.id);
                    setQtys({});
                  }}
                >
                  <p className="font-mono text-sm">{s.saleNo}</p>
                  <p className="text-xs text-muted-foreground">
                    {s.partnerName ?? t('pos.walkIn')} · <Amount value={s.total} currency={baseCurrency} className="text-xs" />
                  </p>
                </button>
              </li>
            ))}
          </ul>
          <div className="mt-3">
            <Pager
              page={sales.data?.page ?? 1}
              pages={range.pages}
              first={range.first}
              last={range.last}
              total={range.total}
              onPage={setPage}
              label={t('pos.salesPages')}
            />
          </div>
        </section>

        <section className="overflow-y-auto p-4">
          {/* ERR-SAL-06-07 — the returns pane says what to do before a sale is picked. */}
          {!sale ? (
            <p className="py-10 text-center text-sm text-muted-foreground">{t('pos.pickSaleHint')}</p>
          ) : (
            <>
              <h2 className="mb-2 text-sm font-medium">{t('pos.pickItems')}</h2>
              <ul className="flex flex-col divide-y">
                {sale.lines.map((line) => {
                  // BR-SAL-06-09: capped at what is left, counting earlier refunds.
                  const remaining = Number(line.qty) - Number(line.refundedQty);
                  const chosen = qtys[line.id] ?? 0;
                  return (
                    <li key={line.id} className="flex flex-col gap-1 py-2">
                      <div className="flex items-center gap-2">
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm">{line.itemName}</p>
                        <p className="text-xs text-muted-foreground">
                          <Amount value={line.unitPrice} currency={baseCurrency} className="text-xs" /> · {t('pos.sold', { n: remaining })}
                        </p>
                      </div>
                      <Button variant="outline" size="icon" aria-label={t('pos.less', { name: line.itemName })}
                        onClick={() => setQtys((q) => ({ ...q, [line.id]: Math.max(0, chosen - 1) }))}>−</Button>
                      <span className="w-8 text-center font-mono">{chosen}</span>
                      <Button variant="outline" size="icon" aria-label={t('pos.more', { name: line.itemName })}
                        onClick={() => setQtys((q) => ({ ...q, [line.id]: Math.min(remaining, chosen + 1) }))}>+</Button>
                      </div>
                      {/* Which unit is coming back. Only when it is going back
                          on the shelf: goods written off restock nothing, so
                          there is nothing to identify. */}
                      {restock && chosen > 0
                        ? line.tracking
                            .filter((block) => block.tracking === 'serial')
                            .map((block) => {
                              const key = `${line.id}|${block.componentItemId ?? ''}`;
                              const picked = comingBack[key] ?? [];
                              return (
                                <div key={key} className="flex flex-wrap items-center gap-1">
                                  <span className="text-xs text-muted-foreground">
                                    {t('pos.whichComesBack', {
                                      name: block.itemName,
                                      picked: picked.length,
                                      sold: block.serialNos.length,
                                    })}
                                  </span>
                                  {block.serialNos.map((serial) => (
                                    <button
                                      key={serial}
                                      type="button"
                                      aria-pressed={picked.includes(serial)}
                                      className={cn(
                                        'rounded border px-1.5 py-0.5 font-mono text-[11px]',
                                        picked.includes(serial)
                                          ? 'border-primary bg-primary/10'
                                          : 'bg-muted',
                                      )}
                                      onClick={() =>
                                        setComingBack((current) => ({
                                          ...current,
                                          [key]: picked.includes(serial)
                                            ? picked.filter((p) => p !== serial)
                                            : [...picked, serial],
                                        }))
                                      }
                                    >
                                      <bdi dir="ltr">{serial}</bdi>
                                    </button>
                                  ))}
                                </div>
                              );
                            })
                        : null}
                    </li>
                  );
                })}
              </ul>

              <label className="mt-3 flex items-center gap-2 text-sm">
                <input type="checkbox" checked={restock} onChange={(e) => setRestock(e.target.checked)} />
                {t('pos.restock')}
              </label>

              <div className="mt-2 flex gap-2">
                {(['cash', 'card'] as const).map((m) => (
                  <Button key={m} variant={method === m ? 'default' : 'outline'} onClick={() => setMethod(m)}>
                    {t(`pos.method_${m}`)}
                  </Button>
                ))}
              </div>

              <p className="mt-3 flex justify-between text-base font-semibold">
                <span>{t('pos.refundTotal')}</span>
                <Amount value={refundTotal} currency={baseCurrency} />
              </p>

              {/*
                * The refusal strip, THROUGH THE MAP — and, since D46, through
                * the wire's own structure. It used to render the server's
                * sentence raw, which meant an Arabic cashier met English on
                * every refusal this endpoint can raise — the refund cap
                * (BR-SAL-06-09) and, since D12, the supervisor-approval
                * limit. `localizeServerMessage` returns the sentence
                * unchanged when nothing maps it, so wrapping is never worse
                * than not wrapping. The third argument is D46's half: a
                * refusal carrying `key`+`params` (the approval limit names
                * the company's own threshold) renders through ICU in the
                * reader's language, at ANY threshold — the old exact-match
                * entry answered only the default and was retired with the
                * conversion.
                */}
              {refund.isError ? (
                <p role="alert" className="mt-2 text-sm text-destructive">
                  {localizeServerMessage(t, (refund.error as Error).message, refund.error)}
                </p>
              ) : null}

              <Button
                className="mt-3 w-full"
                disabled={!isPayable(refundTotal) || refund.isPending}
                onClick={() =>
                  refund.mutate(
                    {
                      saleId: sale.id,
                      lines: Object.entries(qtys)
                        .filter(([, q]) => q > 0)
                        .map(([saleLineId, q]) => ({
                          saleLineId,
                          qty: String(q),
                          ...returningTracking(
                            sale.lines.find((l) => l.id === saleLineId)?.tracking ?? [],
                            saleLineId,
                            comingBack,
                          ),
                        })),
                      restock,
                      method,
                    },
                    {
                      onSuccess: () => {
                        setSaleId(null);
                        setQtys({});
                        setComingBack({});
                      },
                    },
                  )
                }
              >
                {t('pos.processRefund')}
              </Button>
            </>
          )}
        </section>
      </div>
    </main>
  );
}

/** §5h's close dialog — expected against counted, and the explanation. */
function CloseShiftButton({ shift }: { shift: { id: string; expectedCash: string; parkedCount: number } }) {
  const { t } = useTranslation();
  // GEN-107: the till prints in the company's own money, read from the
  // session. A shop has no document currency of its own — SAL-06's contract
  // carries none — so what is stated here is exactly what the sale POSTS in
  // (`pos.service.ts` reads the same fact). Nothing converts (GEN-116).
  const baseCurrency = useBaseCurrency();
  const close = useCloseShift(shift.id);
  const [open, setOpen] = useState(false);
  const [counted, setCounted] = useState('');
  const [reason, setReason] = useState('');

  const difference = drawerDifference(counted, shift.expectedCash);

  return (
    <>
      <Button variant="outline" onClick={() => setOpen(true)}>{t('pos.closeShift')}</Button>
      {open ? (
        <Dialog title={t('pos.closeShift')} onClose={() => setOpen(false)}>
          <p className="text-sm text-muted-foreground">{t('pos.countHint')}</p>
          <dl className="my-3 flex flex-col gap-1 text-sm">
            <div className="flex justify-between"><dt>{t('pos.expected')}</dt><dd><Amount value={shift.expectedCash} currency={baseCurrency} /></dd></div>
          </dl>
          <label className="flex flex-col gap-1 text-sm" htmlFor="counted-cash">
            {t('pos.counted')}
            <input
              id="counted-cash"
              dir="ltr"
              inputMode="decimal"
              className="rounded-md border bg-background px-3 py-2 text-end font-mono"
              value={counted}
              onChange={(e) => setCounted(e.target.value)}
            />
          </label>
          <p className="mt-2 text-sm">
            {t('pos.difference')}: <Amount value={difference} currency={baseCurrency} />
          </p>

          {/* BR-SAL-06-12: a difference other than zero demands a typed
              explanation. The server refuses without one; asking here means the
              cashier is not refused after the fact. */}
          {counted !== '' && isDrawerOut(difference) ? (
            <label className="mt-2 flex flex-col gap-1 text-sm" htmlFor="close-reason">
              {t('pos.explain')}
              <input
                id="close-reason"
                className="rounded-md border bg-background px-3 py-2"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
            </label>
          ) : null}

          {close.isError ? (
            // THROUGH THE MAP, AND THIS IS THE WRAP THAT REVIVES TWO ENTRIES.
            // `pos.errNothingToBank` and `pos.errNoDepositAccount` have been in
            // `STATIC_MESSAGE_KEYS` since the A21 deposit work, and NOTHING has
            // ever reached them: the close-shift door is the only place either
            // sentence is raised, and it rendered the server's string raw. The
            // entries were correct and inert — an Arabic cashier met English on
            // every failed close. `explainDiscrepancy` and `parkedSalesOpen`
            // also arrive here and are parameterised, so they still pass
            // through in English by the documented policy.
            <p role="alert" className="mt-2 text-sm text-destructive">
              {localizeServerMessage(t, (close.error as Error).message, close.error)}
            </p>
          ) : null}

          <Button
            className="mt-4 w-full"
            disabled={counted === '' || (isDrawerOut(difference) && !reason.trim()) || close.isPending}
            onClick={() =>
              close.mutate(
                { countedCash: counted, ...(reason.trim() ? { discrepancyReason: reason.trim() } : {}) },
                { onSuccess: () => setOpen(false) },
              )
            }
          >
            {t('pos.confirmClose')}
          </Button>
        </Dialog>
      ) : null}
    </>
  );
}

function Dialog({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" role="dialog" aria-label={title}>
      <div className="w-full max-w-md rounded-xl border bg-background p-4 shadow-lg">
        <div className="mb-2 flex items-center justify-between">
          <h2 className="text-base font-semibold">{title}</h2>
          <button type="button" aria-label="Close" onClick={onClose} className="text-muted-foreground">×</button>
        </div>
        {children}
      </div>
    </div>
  );
}
